/**
 * Admin Management Service
 * API calls for managing admin users and invitations
 */

/**
 * Get the admin API base URL
 * In development: uses proxy (/api/admin)
 * In production: uses full Cloud Function URL
 */
function getAdminApiUrl(): string {
    const apiUrl = import.meta.env.VITE_ADMIN_API_URL;
    if (apiUrl) {
        return apiUrl;
    }
    // Fallback to proxy for development
    return '/api/admin';
}

const API_BASE_URL = `${getAdminApiUrl()}/admin`;

/**
 * Get authorization token for admin API calls with auto-refresh
 */
async function getAuthToken(): Promise<string> {
    const { getAdminJWT } = await import('./adminAuthService');
    return await getAdminJWT();
}

interface AdminUser {
    uid: string;
    email: string;
    displayName: string | null;
    role: 'support_agent' | 'ops_manager' | 'super_admin';
    mfaEnabled: boolean;
    createdAt: string;
    lastLogin: string | null;
    createdBy: string | null;
}

interface AdminInvitation {
    token: string;
    email: string;
    role: string;
    createdBy: string;
    createdAt: string;
    expiresAt: string;
    expired: boolean;
    registrationUrl: string;
}

/**
 * Get all admin users
 */
export async function listAdmins(): Promise<AdminUser[]> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}/admins`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch admins');
    }

    const data = await response.json();
    return data.admins;
}

/**
 * Invite new admin user
 */
export async function inviteAdmin(email: string, role: string): Promise<{ registrationUrl: string; token: string }> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}/admins/invite`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, role }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create invitation');
    }

    const data = await response.json();
    return data.invitation;
}

/**
 * Get pending invitations
 */
export async function listInvitations(): Promise<AdminInvitation[]> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}/admins/invitations`, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch invitations');
    }

    const data = await response.json();
    return data.invitations;
}

/**
 * Update admin user
 */
export async function updateAdmin(
    uid: string,
    updates: { role?: string; displayName?: string; mfaEnabled?: boolean }
): Promise<void> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}/admins/${uid}`, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update admin');
    }
}

/**
 * Delete admin user (revoke access)
 */
export async function deleteAdmin(uid: string): Promise<void> {
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}/admins/${uid}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to revoke admin access');
    }
}

/**
 * Cancel invitation
 */
export async function cancelInvitation(token: string): Promise<void> {
    const jwtToken = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}/admins/invitations/${token}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${jwtToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel invitation');
    }
}

/**
 * Register admin with invitation token (public endpoint)
 */
export async function registerAdmin(token: string, password: string, displayName?: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admins/register`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password, displayName }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to register admin account');
    }
}

/**
 * Format role for display
 */
export function formatRole(role: string): string {
    const roleMap: Record<string, string> = {
        support_agent: 'Support Agent',
        ops_manager: 'Operations Manager',
        super_admin: 'Super Admin',
    };
    return roleMap[role] || role;
}

/**
 * Get role badge color
 */
export function getRoleBadgeColor(role: string): string {
    const colorMap: Record<string, string> = {
        support_agent: 'bg-blue-100 text-blue-800',
        ops_manager: 'bg-purple-100 text-purple-800',
        super_admin: 'bg-red-100 text-red-800',
    };
    return colorMap[role] || 'bg-gray-100 text-gray-800';
}
